import React, { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  section?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[RouteErrorBoundary:${this.props.section ?? 'unknown'}]`, error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center min-h-[300px]">
          <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
          <h3 className="font-display font-bold text-lg text-foreground">
            {this.props.section ? `${this.props.section} hit a snag` : 'Something went wrong'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            This section crashed, but the rest of the app is still working fine.
          </p>
          {this.state.error && (
            <pre className="text-xs text-muted-foreground bg-muted rounded-lg p-2 mt-3 overflow-auto max-h-20 max-w-sm text-left">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-2 mt-4">
            <Button onClick={this.handleReset} variant="outline" size="sm" className="font-display font-bold">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Retry
            </Button>
            <Button onClick={() => window.location.reload()} size="sm" className="font-display font-bold">
              <Home className="w-3.5 h-3.5 mr-1.5" />
              Reload
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
